import Emitter from "@homer.core/emitter";
import Channel from "@homer.core/channel";
import deepobjectdiff from "@homer.adapter/deepobjectdiff";
import scheduler from "@homer.core/scheduler";
import persistedEmail from "@homer.core/email";
import StorageCookie from "@homer.core/storage.cookie";
import StorageLocal from "@homer.core/storage.local";
import homer from "@homer.api/homer";
import application from "@homer.core/application";
import { name } from "../package.json";

const channel = Channel.obtain(name);
const classes = document.documentElement.classList;
const storageCookie = new StorageCookie(name, { version: application.version });
const storageLocal = new StorageLocal(name, { version: application.version });
const flag = "isUser";
const state = {
    current: Object.freeze(JSON.parse(storageLocal.get() || "null")),
    previous: Object.freeze(null),
};
const detailsAutomatic = Object.freeze({ method: "automatic" });

if (!state.current) {
    storageCookie.remove();
}

class User extends Emitter {
    constructor() {
        super();

        this.on(/^((?!(change)).)+$/, (event, current, previous, details, isExternal = false) => {
            scheduler.yield.medium().then(() => {
                if (current) {
                    classes.add(flag);
                } else {
                    classes.remove(flag);
                }
            });

            if (!deepobjectdiff.isEqual(previous, current)) {
                state.previous = previous;
                state.current = current;

                scheduler.yield.medium().then(() => {
                    if (!isExternal) {
                        const stringifiedCurrent = JSON.stringify(current);

                        storageLocal.set(stringifiedCurrent);

                        if (current) {
                            storageCookie.set(stringifiedCurrent);
                        } else {
                            storageCookie.remove();
                        }
                    }

                    this.emit("change", current, previous, details, isExternal);
                });
            }

            if (!isExternal) {
                const payload = { id: event.name, current, previous };

                if (details) {
                    payload.details = details;
                }

                channel.broadcast(payload);
            }
        }).on(/^(?:login|update|retrieve)$/, (event, current) => {
            if (current) {
                void persistedEmail.set(current.email, event.name, event.name === "retrieve");
            } else if (event.name === "retrieve" && persistedEmail.plain) {
                void persistedEmail.set(persistedEmail.plain, persistedEmail.method, true);
            }
        });

        channel.on("broadcast", (event, payload, isExternal) => {
            if (isExternal) {
                const { id, current, previous, details } = payload;

                this.emit(id, current, previous, details, isExternal);
            }
        });

        if (state.current) {
            void this.retrieve();

            return this;
        }

        return this.retrieve();
    }

    get current() {
        return state.current;
    }

    get previous() {
        return state.previous;
    }

    retrieve(details = detailsAutomatic) {
        return homer.client.post("/user/retrieve").then(async (response) => {
            const previous = state.current;
            const current = Object.freeze(response.user);

            await this.emit("retrieve", current, previous);

            if (!!current !== !!previous) {
                // Give Promise-backed imports a chance to attach listeners before the derived signal fires.
                // The retrieve event already applied the state transition; emit only the derived boundary signal.
                void scheduler.yield.medium().then(() => this.emit(current ? "login" : "logout", current, current, details));
            }

            return this;
        });
    }
}

export default new User();
